import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { DetailHeader } from "@/components/layout/detail-header";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";
import { Trash2 } from "lucide-react";
import type { Expectation, RunOnOption } from "@/lib/types/test";

interface Test {
  id: string;
  name: string;
  description?: string;
  group?: string;
  isAutomatable: boolean;
  createdAt: string;
  credentials?: {
    apiKey?: string;
  };
}

const RUN_ON_OPTIONS: RunOnOption[] = [
  { value: 'FIRST_X_RECORDS', label: 'First X Records' },
  { value: 'SPECIFIC_X_RECORDS', label: 'Specific Records X-Y' },
  { value: 'RANDOM_X_RECORDS', label: 'Random X Records' },
  { value: 'SINGLE_RECORD', label: 'Single Record' }
];

export function TestExpectationsPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  
  const [test, setTest] = useState<Test | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expectations, setExpectations] = useState<Expectation[]>([]);
  const [isLoadingExpectations, setIsLoadingExpectations] = useState(false);
  const [expectationFilter, setExpectationFilter] = useState("");
  const [originalExpectations, setOriginalExpectations] = useState<Record<string, Partial<Expectation>>>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expectationToDelete, setExpectationToDelete] = useState<Expectation | null>(null);
  const [dropdownStates, setDropdownStates] = useState<Record<string, { runOn?: boolean; expectationState?: boolean }>>({});
  const [isDeletingExpectation, setIsDeletingExpectation] = useState(false);

  // Load test data
  useEffect(() => {
    if (testId) {
      loadTest();
      loadExpectations();
    }
  }, [testId]);

  const loadTest = async () => {
    if (!testId) return;
    
    try {
      setIsLoading(true);
      const testData = await api.get<Test>(`/test/${testId}`);
      setTest(testData);
    } catch (error) {
      console.error("Failed to load test:", error);
      showError("Failed to load test", "Could not load test details. Please try again.");
      navigate("/app/tests");
    } finally {
      setIsLoading(false);
    }
  };

  const loadExpectations = async () => {
    if (!testId) return;
    
    try {
      setIsLoadingExpectations(true);
      setExpectations([]);
      setOriginalExpectations({});
      
      const data = await api.get<Expectation[]>(`/test/${testId}/expectations`);
      const expectationsArray = Array.isArray(data) ? data : [];
      
      setExpectations(expectationsArray);
      
      // Store original state for change tracking
      const originalState: Record<string, Partial<Expectation>> = {};
      expectationsArray.forEach(exp => {
        originalState[exp.id] = {
          expectation: exp.expectation,
          runOn: exp.runOn,
          recordCount: exp.recordCount,
          expectationState: exp.expectationState
        };
      });
      setOriginalExpectations(originalState);
    } catch (error) {
      console.error('Error loading expectations:', error);
      showError("Error loading expectations", "Could not load expectations. Please try again.");
      setExpectations([]);
      setOriginalExpectations({});
    } finally {
      setIsLoadingExpectations(false);
    }
  };

  const addExpectation = async () => {
    if (!testId) return;
    
    try {
      const newExpectation = await api.post<Expectation>(`/test/${testId}/expectations`, {
        expectation: '',
        runOn: 'FIRST_X_RECORDS',
        recordCount: '10',
        expectationState: 'PASSED'
      });
      
      setExpectations(prev => [...prev, newExpectation]);
      setOriginalExpectations(prev => ({
        ...prev,
        [newExpectation.id]: {
          expectation: newExpectation.expectation,
          runOn: newExpectation.runOn,
          recordCount: newExpectation.recordCount,
          expectationState: newExpectation.expectationState
        }
      }));
      showSuccess("Expectation created successfully");
    } catch (error) {
      console.error('Error creating expectation:', error);
      showError("Failed to create expectation", "Could not create expectation. Please try again.");
    }
  };

  const updateExpectation = async (exp: Expectation) => {
    if (!testId || !hasExpectationChanged(exp)) return;
    
    try {
      await api.patch(`/test/${testId}/expectations/${exp.id}`, {
        expectation: exp.expectation,
        runOn: exp.runOn,
        recordCount: exp.recordCount,
        expectationState: exp.expectationState
      });
      
      // Update original state after successful save
      setOriginalExpectations(prev => ({
        ...prev,
        [exp.id]: {
          expectation: exp.expectation,
          runOn: exp.runOn,
          recordCount: exp.recordCount,
          expectationState: exp.expectationState
        }
      }));
      
      showSuccess("Expectation updated successfully");
    } catch (error) {
      console.error('Error updating expectation:', error);
      showError("Failed to update expectation", "Could not update expectation. Please try again.");
    }
  };

  const deleteExpectation = async () => {
    if (!testId || !expectationToDelete) return;
    
    try {
      setIsDeletingExpectation(true);
      await api.delete(`/test/${testId}/expectations/${expectationToDelete.id}`);
      
      setExpectations(prev => prev.filter(e => e.id !== expectationToDelete.id));
      setOriginalExpectations(prev => {
        const { [expectationToDelete.id]: removed, ...rest } = prev;
        return rest;
      });
      
      showSuccess("Expectation deleted successfully");
      setShowDeleteModal(false);
      setExpectationToDelete(null);
    } catch (error) {
      console.error('Error deleting expectation:', error);
      showError("Failed to delete expectation", "Could not delete expectation. Please try again.");
    } finally {
      setIsDeletingExpectation(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setExpectationToDelete(null);
  };

  const hasExpectationChanged = (exp: Expectation) => {
    const original = originalExpectations[exp.id];
    if (!original) return true;
    
    return original.expectation !== exp.expectation ||
           original.runOn !== exp.runOn ||
           original.recordCount !== exp.recordCount ||
           original.expectationState !== exp.expectationState;
  };

  const updateExpectationField = (id: string, field: keyof Expectation, value: any) => {
    setExpectations(prev => prev.map(exp => 
      exp.id === id ? { ...exp, [field]: value } : exp
    ));
  };

  const toggleDropdown = (expId: string, type: 'runOn' | 'expectationState') => {
    setDropdownStates(prev => ({
      ...prev,
      [expId]: {
        ...prev[expId],
        [type]: !prev[expId]?.[type]
      }
    }));
  };

  const closeAllDropdowns = () => {
    setDropdownStates({});
  };

  const getRecordCountPlaceholder = (runOn: string) => {
    switch (runOn) {
      case 'FIRST_X_RECORDS':
        return 'Number of records';
      case 'SPECIFIC_X_RECORDS':
        return 'Record range (e.g., 1-10)';
      case 'RANDOM_X_RECORDS':
        return 'Number of records';
      case 'SINGLE_RECORD':
        return 'Record number';
      default:
        return 'Number of records';
    }
  };

  const filteredExpectations = expectations.filter(exp => {
    if (!expectationFilter) return true;
    const searchTerm = expectationFilter.toLowerCase();
    return exp.expectation.toLowerCase().includes(searchTerm) ||
           exp.externalId.toLowerCase().includes(searchTerm);
  });

  const handleToggleChange = (tabId: string) => {
    if (tabId === "details") {
      navigate(`/app/tests/${testId}`);
    } else {
      navigate(`/app/tests/${testId}/${tabId}`);
    }
  };

  const confirmDelete = (exp: Expectation) => {
    setExpectationToDelete(exp);
    setShowDeleteModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Loading test details...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Test not found</div>
        </div>
      </div>
    );
  }

  const breadcrumb = [
    { label: "All Tests", href: "/app/tests" },
    { label: test.name }
  ];

  const toggleOptions = [
    { id: "details", label: "Details" },
    { id: "explorer", label: "Explorer" },
    { id: "files", label: "Files" },
    { id: "flow", label: "Flow" },
    { id: "expectations", label: "Expectations" },
    { id: "runs", label: "Runs" },
  ];

  const headerActions = (
    <Button
      onClick={addExpectation}
      size="sm"
      className="text-xs"
    >
      New Expectation
    </Button>
  );

  return (
    <div className="flex flex-col h-full" onClick={closeAllDropdowns}>
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="expectations"
        onToggleChange={handleToggleChange}
        actions={headerActions}
        searchValue={expectationFilter}
        onSearchChange={setExpectationFilter}
        searchPlaceholder="Filter expectations"
      />

      <div className="flex-1 overflow-auto p-6">
        {/* Loading State */}
        {isLoadingExpectations && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        )}

        {/* Expectations List */}
        {!isLoadingExpectations && (
          <div className="space-y-4">
            {filteredExpectations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {expectations.length === 0 
                  ? 'No expectations defined. Click "New Expectation" to create one.'
                  : 'No expectations match your filter.'
                }
              </div>
            ) : (
              filteredExpectations.map((exp) => (
                <div key={exp.id} className="border rounded-lg bg-card">
                  {/* Single row with ID, Labels, and Controls */}
                  <div className="flex items-center justify-between p-4 border-b" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-6">
                      {/* ID */}
                      <span className="text-sm text-muted-foreground font-mono shrink-0 text-right min-w-[60px]">#{exp.externalId}</span>
                      
                      {/* Run On Dropdown with Record Count - Combined Component */}
                      <div className="flex items-center gap-2 min-w-0">
                        <label className="text-xs text-muted-foreground shrink-0">Run On:</label>
                        <div className="flex border rounded focus-within:ring-1 focus-within:ring-primary">
                          {/* Run On Dropdown */}
                          <div className="relative">
                            <button
                              onClick={() => toggleDropdown(exp.id, 'runOn')}
                              className="px-3 py-1 text-xs bg-background hover:bg-accent flex items-center justify-between min-w-[180px] border-r"
                            >
                              <span className="truncate">{RUN_ON_OPTIONS.find(opt => opt.value === exp.runOn)?.label || exp.runOn}</span>
                              <svg className="w-3 h-3 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {dropdownStates[exp.id]?.runOn && (
                              <div className="absolute top-full left-0 mt-1 bg-popover border rounded-md shadow-md z-10 min-w-[180px]">
                                {RUN_ON_OPTIONS.map(option => (
                                <button
                                  key={option.value}
                                  onClick={() => {
                                    updateExpectationField(exp.id, 'runOn', option.value);
                                    closeAllDropdowns();
                                  }}
                                  className="block w-full text-left px-3 py-2 text-xs hover:bg-accent"
                                >
                                  {option.label}
                                </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Record Count Input */}
                          <input
                            type="text"
                            value={exp.recordCount}
                            onChange={(e) => updateExpectationField(exp.id, 'recordCount', e.target.value)}
                            placeholder={getRecordCountPlaceholder(exp.runOn)}
                            className="px-3 py-1 text-xs bg-background w-24 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Expected Result Dropdown */}
                      <div className="flex items-center gap-2 min-w-0">
                        <label className="text-xs text-muted-foreground shrink-0">Expected:</label>
                        <div className="relative">
                          <button
                            onClick={() => toggleDropdown(exp.id, 'expectationState')}
                            className="px-3 py-1 text-xs border rounded hover:bg-accent flex items-center justify-between min-w-[100px]"
                          >
                            <span>{exp.expectationState}</span>
                            <svg className="w-3 h-3 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {dropdownStates[exp.id]?.expectationState && (
                            <div className="absolute top-full left-0 mt-1 bg-popover border rounded-md shadow-md z-10 min-w-[100px]">
                              {['PASSED', 'FAILED'].map(state => (
                                <button
                                  key={state}
                                  onClick={() => {
                                    updateExpectationField(exp.id, 'expectationState', state);
                                    closeAllDropdowns();
                                  }}
                                  className="block w-full text-left px-3 py-2 text-xs hover:bg-accent"
                                >
                                  {state}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        onClick={() => updateExpectation(exp)}
                        disabled={!hasExpectationChanged(exp)}
                        size="sm"
                        variant="secondary"
                        className="text-xs h-7"
                      >
                        Save
                      </Button>
                      <button
                        onClick={() => confirmDelete(exp)}
                        className="px-2 py-1 text-xs text-destructive hover:bg-muted rounded-md cursor-pointer"
                        title="Delete expectation"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expectation Text Area */}
                  <div className="p-4">
                    <label className="block text-sm font-medium mb-2">Expectation</label>
                    <textarea
                      value={exp.expectation}
                      onChange={(e) => updateExpectationField(exp.id, 'expectation', e.target.value)}
                      placeholder="Enter your expectation..."
                      className="w-full px-3 py-2 border border-input rounded-md min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={deleteExpectation}
        title="Delete Expectation"
        message={`Are you sure you want to delete expectation #${expectationToDelete?.externalId}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeletingExpectation}
      />
    </div>
  );
}
